import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface RegulatoryBody {
  id: string;
  name: string;
  acronym: string;
  country: string;
  region: string | null;
  type: "tier-1" | "tier-2" | "offshore" | "regional";
  website: string | null;
  description: string | null;
  enforcement_power: string | null;
  compensation_scheme: string | null;
  leverage_restrictions: string | null;
  client_fund_protection: string | null;
  established_year: number | null;
  licenses_count: number | null;
  is_active: boolean;
  logo_url: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const useRegulators = () => {
  return useQuery({
    queryKey: ["regulatory_bodies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regulatory_bodies")
        .select("*")
        .order("acronym");
      if (error) throw error;
      return data as RegulatoryBody[];
    },
  });
};

export const useRegulatorByAcronym = (acronym: string) => {
  return useQuery({
    queryKey: ["regulatory_body", acronym],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regulatory_bodies")
        .select("*")
        .ilike("acronym", acronym)
        .maybeSingle();
      if (error) throw error;
      return data as RegulatoryBody | null;
    },
    enabled: !!acronym,
  });
};

export const tierConfig = {
  "tier-1": { label: "Tier-1", colorClass: "text-primary border-primary/30 bg-primary/10" },
  "tier-2": { label: "Tier-2", colorClass: "text-secondary border-secondary/30 bg-secondary/10" },
  offshore: { label: "Offshore", colorClass: "text-destructive border-destructive/30 bg-destructive/10" },
  regional: { label: "Regional", colorClass: "text-wire-purple border-wire-purple/30 bg-wire-purple/10" },
} as const;

export const regionList = ["All", "Europe", "North America", "Asia-Pacific", "Middle East", "Africa", "Caribbean", "Central America"] as const;
export const typeList = ["All", "tier-1", "tier-2", "offshore", "regional"] as const;
